
import React from "react";

import { Interval } from "luxon";

import * as d3 from 'd3';

import logo from './img/TA-Logo-TS-banner_copy.png';

import svgToCanvas from "./svgToCanvas";
import './MapOverlayWidget.css';


const ATTRIBUTION_LINKS = {
    'Sources': 'https://tornadoarchive.com/home/tornado-archive-data-explorer/sources/',
    'ESWD': 'https://eswd.eu',
    'NIWA': 'https://hwe.niwa.co.nz',
}

class MapOverlayWidget extends React.PureComponent {
    constructor(props) {
        super(props);

        this.injuries = 0;
        this.fatalities = 0;
        this.injuries_atleast = false;
        this.fatalities_atleast = false;
        this.ntors = 0;
    }

    createColorbar() {
        if (this.overlay === null) return; // Not sure why this happens

        const root_rect = this.overlay.getBoundingClientRect();
        const default_svg_height = 600;

        const font_name = 'Trebuchet MS';
        const svg_height = Math.min(default_svg_height, root_rect['height'] * 0.75);
        const svg_width = svg_height / 9;

        const scale_fact = svg_height / default_svg_height;

        const left = 53 * scale_fact;
        const bottom = 5 * scale_fact;
        const height = svg_height - 2 * bottom;
        const width = 10 * scale_fact;

        let root = d3.select(this.overlay);
        root.selectAll('#colorbar').remove();

        const colormaps = this.props['env_handler'].getColormaps(this.props['env_type']);
        if (colormaps.length == 0 || this.props['sel_data_time'] === null) {
            return;
        }

        if (colormaps.length > 1) {
            console.warn("More than one colormap. Using the first one.");
        }

        const colormap = colormaps[0];
        const colorbar_info = colormap.getColorbarInfo();

        var y_scale = d3.scaleBand()
            .domain(colormap.getLevels())
            .range([height, 0]);

        let svg = root.append('div')
                      .attr('id', 'colorbar')
                      .attr('class', this.props['controls_visible'] ? '' : 'controls-hidden')
                      .style('width', svg_width)
                      .style('height', svg_height)
                      .append('svg')
                      .attr('width', svg_width)
                      .attr('height', svg_height);

        svg.append('g')
            .selectAll('g')
            .data(colormap.getMap())
            .enter()
                .append('rect')
                .attr('x', left)
                .attr('y', d => y_scale(d['level']) + bottom)
                .attr('width', width)
                .attr('height', y_scale.bandwidth())
                .attr('fill', d => d['color'])
                .attr('opacity', d => d['opacity']);

        svg.append('g')
             .attr('transform', 'translate(' + left + ', ' + bottom + ')')
             .call(d3.axisLeft(y_scale)
                     .tickValues(colorbar_info['ticks']))

        svg.selectAll('.domain').remove();

        svg.append('text')
             .attr('transform', 'translate(' + (15 * scale_fact) + ', ' + (bottom + height / 2) + ') rotate(-90)')
             .attr('text-anchor', 'middle')
             .attr('fill', 'black')
             .attr('font-size', 16 * scale_fact)
             .text(colorbar_info['label'])

        svg.append('rect')
            .attr('x', left)
            .attr('y', bottom)
            .attr('width', width - 1)
            .attr('height', height)
            .attr('stroke', '#000000')
            .attr('fill', 'none');

        svg.selectAll('text')
            .attr('font-family', font_name);

        d3.selectAll(".tick > text")
            .each(function(d, i){
              d3.select(this).style("font-size", (12 * scale_fact) + 'px');
            });
    }

    windowResize() {
        this.createColorbar();
        this.updateStatistics();
    }

    componentDidMount() {
        this.props['setscreenshotfunc'](this.screenshot.bind(this), 'overlay');
        this.createColorbar();

        window.addEventListener('resize', this.windowResize.bind(this));
        this.updateStatistics();

        this.updateAttribution();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.windowResize.bind(this));
    }

    componentDidUpdate(prev_props) {
        if (this.props['controls_visible'] != prev_props['controls_visible']) {
            this.createColorbar();
        }

        if (this.props['env_type'] != prev_props['env_type'] 
            || this.props['sel_data_time'] === null || prev_props['sel_data_time'] === null) {
            this.createColorbar();
        }

        //console.log(this.props['tor_coll'], prev_props['tor_coll']);
        if (this.props['tor_coll'] !== prev_props['tor_coll'] || this.props['interval'] !== prev_props['interval'] 
            || this.props['temp_display'] !== prev_props['temp_display']) {
            this.updateStatistics();
        }

        this.updateAttribution();
    }

    async updateStatistics() {
        const qc_injfat = v => v !== undefined && Number.isInteger(v) && v >= 0;

        if (this.props['temp_display'] == 'None') {
            this.ntor_el.innerHTML = "";
            this.ninj_el.innerHTML = "";
            this.nfat_el.innerHTML = "";
            return;
        }

        if (this.props['tor_coll'] !== null) {
            this.ntors = (await this.props['tor_coll'].getNTornadoes([this.props['interval']], undefined, true))[0]['count'];
            this.injuries = 0;
            this.fatalities = 0;

            if (this.ntors > 0) {
                const injuries_prom = this.props['tor_coll'].getPropertyValues('injuries', this.props['interval']);
                const fatalities_prom = this.props['tor_coll'].getPropertyValues('fatalities', this.props['interval']);

                const injuries = (await injuries_prom)['injuries'];
                const fatalities = (await fatalities_prom)['fatalities'];

                this.injuries_atleast = injuries.filter(v => !qc_injfat(v)).length > 0;
                this.fatalities_atleast = fatalities.filter(v => !qc_injfat(v)).length > 0;

                this.injuries = injuries.filter(qc_injfat).reduce((a, b) => a + b, 0);
                this.fatalities = fatalities.filter(qc_injfat).reduce((a, b) => a + b, 0);
            }

            let num_header, inj_header, fat_header;
            if (this.props['window_size']['width'] >= 700) {
                num_header = "Number of Tornadoes: ";
                inj_header = "Injuries: ";
                fat_header = "Fatalities: ";
            }
            else {
                num_header = "Num: ";
                inj_header = "Inj: ";
                fat_header = "Fat: ";
            }
            
            if (this.ntor_el !== null && this.ninj_el !== null && this.nfat_el !== null) {
                this.ntor_el.innerHTML = num_header + this.ntors;
                this.ninj_el.innerHTML = inj_header + (this.injuries_atleast ? '\u2265' : '') + this.injuries;
                this.nfat_el.innerHTML = fat_header + (this.fatalities_atleast ? '\u2265' : '') + this.fatalities;
            }
        }
    }

    updateAttribution() {
        const add_copy =  attr => (attr != 'Sources' ? '&copy; ' : '') + attr;
        const attribution_str = 'Data: ' 
            + this.props['attribution'].map(attr => '<a href="' + ATTRIBUTION_LINKS[attr] + '" target="_blank">' + add_copy(attr) + '</a>')
                                       .join(' | ');

        this.attribution.innerHTML = attribution_str;
    }

    screenshot() {
        const need_colorbar = this.props['sel_data_time'] !== null && this.props['env_type'] !== null;
        const ta_logo_promise = new Promise((resolve, reject) => {
            let img = new Image();
            img.onload = () => resolve(img);
            img.src = logo;
        });
        
        const mapbox_logo_promise = new Promise((resolve, reject) => {
            let img = new Image();
            img.onload = () => resolve(img);

            const a = document.querySelector('a.mapboxgl-ctrl-logo');
            const style = window.getComputedStyle(a, false);
            const dataURL = style.backgroundImage.slice(5, -2);

            img.src = dataURL;
        });

        let promises = [ta_logo_promise, mapbox_logo_promise];

        if (need_colorbar) {
            const colorbar_promise = new Promise((resolve, reject) => {
                const dpr = window.devicePixelRatio || 1;
                const svg = this.overlay.querySelector('svg')

                const cbar_elem = document.querySelector('#colorbar');
                const bar_bg_rect = cbar_elem.getBoundingClientRect();
                const cbar_style = window.getComputedStyle(cbar_elem);
                let cbar_border_radius = cbar_style.borderBottomRightRadius;
                const cbar_background_color = cbar_style.backgroundColor;

                let canvas = document.createElement('canvas');
                canvas.width = bar_bg_rect['width'] * dpr;
                canvas.height = bar_bg_rect['height'] * dpr;

                const roundRect = (ctx, x, y, w, h, r) => {
                    if (w < 2 * r) r = w / 2;
                    if (h < 2 * r) r = h / 2;

                    ctx.beginPath();
                    ctx.moveTo(x+r, y);
                    ctx.arcTo(x+w, y,   x+w, y+h, r);
                    ctx.arcTo(x+w, y+h, x,   y+h, r);
                    ctx.arcTo(x,   y+h, x,   y,   r);
                    ctx.arcTo(x,   y,   x+w, y,   r);
                    ctx.closePath();
                }

                if (cbar_border_radius.includes('px')) {
                    cbar_border_radius = parseInt(cbar_border_radius.slice(0, -2));
                }

                let ctx = canvas.getContext('2d');
                roundRect(ctx, -cbar_border_radius * dpr, 0, 
                    (bar_bg_rect['width'] + cbar_border_radius) * dpr, bar_bg_rect['height'] * dpr, cbar_border_radius * dpr);

                ctx.fillStyle = cbar_background_color;
                ctx.fill();

                svgToCanvas(svg, canvas, 0, 0, dpr);

                let img = new Image();
                img.onload = () => resolve(img);
                img.src = canvas.toDataURL();
            });

            promises.push(colorbar_promise)
        }

        return Promise.allSettled(promises).then(results => {
            const logo_img = results[0]['value'];
            const mapbox_logo_img = results[1]['value'];
            const colorbar_img = need_colorbar ? results[2]['value'] : null;

            const dpr = window.devicePixelRatio || 1;
            const rect = document.getElementsByClassName('overlay')[0].getBoundingClientRect();

            let canvas = document.createElement('canvas');
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;

            let ctx = canvas.getContext('2d');

            // This is gonna get stoopid
            const header = document.getElementById('map-header');
            const header_rect = header.getBoundingClientRect();
            const header_style = window.getComputedStyle(header);
            const background_color = header_style['backgroundColor'];

            // Draw header background
            ctx.beginPath();
            ctx.fillStyle = background_color;
            ctx.rect(header_rect['x'] * dpr, header_rect['y'] * dpr, header_rect['width'] * dpr, header_rect['height'] * dpr);
            ctx.fill();

            // Create statistics text
            ctx.fillStyle = '#000000';
            ctx.font = (16 * 0.9 * dpr) + 'px Trebuchet MS';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'top';

            const fat_str = (this.fatalities_atleast ? '\u2265' : '') + this.fatalities
            const inj_str = (this.injuries_atleast ? '\u2265' : '') + this.injuries

            if (this.props['temp_display'] != 'None') {
                ctx.fillText('Number of Tornadoes: ' + this.ntors, (header_rect['width'] - 8) * dpr, 2 * dpr);
                ctx.fillText('Fatalities: ' + fat_str + '    Injuries: ' + inj_str, (header_rect['width'] - 8) * dpr, 21 * dpr);
            }

            // Draw TA logo
            const logo_width = logo_img.width;
            const logo_height = logo_img.height;
            const logo_aspect = logo_width / logo_height;
            const logo_draw_height = 41;
            const logo_y = (header_rect['height'] - logo_draw_height) / 2;

            ctx.drawImage(logo_img, 0, logo_y * dpr, logo_draw_height * dpr * logo_aspect, logo_draw_height * dpr);

            // Create date and filter header text
            const [date_header_str, filter_str] = this._createMapHeaders();
            let env_data_str = "";
            if (need_colorbar) {
                env_data_str = this.props['env_handler'].getTitle(this.props['env_type']) + " Valid " 
                    + this.props['sel_data_time'].toFormat("HHmm 'UTC' MMM d, yyyy");
            }

            ctx.textAlign = 'left';
            let y = 2 * dpr;

            if (date_header_str != "") {
                ctx.fillText(date_header_str, 140 * dpr, y);
                y += 17 * dpr;
            }

            if (filter_str != "") {
                ctx.fillText(filter_str, 140 * dpr, y);
                y += 17 * dpr;
            }

            ctx.fillText(env_data_str, 140 * dpr, y);

            if (need_colorbar) {
                const colorbar_height = colorbar_img.height;
                const colorbar_width = colorbar_img.width;
                const colorbar_y = canvas.height / 2 - colorbar_height / 2;
                ctx.drawImage(colorbar_img, 0, colorbar_y, colorbar_width, colorbar_height);
            }

            const fontsize = 14;
            const height = fontsize + 6;

            if (this.props['attribution'].includes('ESWD')) {
                let data_text = 'Data: European Severe Weather Database'; 
                const data_width = (data_text.length + 3) / fontsize * 95;
    
                ctx.fillStyle = 'rgba(255,255,255,0.4)';
                ctx.fillRect(canvas.width - data_width * dpr, canvas.height - 2 * height * dpr, data_width * dpr, height * dpr);

                ctx.fillStyle = 'black';
                ctx.textAlign = 'right';
                ctx.textBaseline = 'bottom';
                ctx.fillText(data_text, canvas.width - 3 * dpr, canvas.height + (-height - 3) * dpr);
            }

            // Add mapbox credits (most of this stolen from https://github.com/mapbox/mapbox-gl-js/pull/6518/files)
            ctx.drawImage(mapbox_logo_img, 3 * dpr, canvas.height - (mapbox_logo_img.height + 4) * dpr, 
                          mapbox_logo_img.width * dpr, mapbox_logo_img.height * dpr);

            let text = '© Mapbox © OpenStreetMap';
            if (canvas.width < 300) text = '© OSM';
            const width = (text.length + 3) / fontsize * 100;

            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.fillRect(canvas.width - width * dpr, canvas.height - height * dpr, width * dpr, height * dpr);

            ctx.font = (fontsize * dpr) + 'px Arial';
            ctx.fillStyle = 'black';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'bottom';
            ctx.fillText(text, canvas.width - (width - 3) * dpr, canvas.height - 3 * dpr);

            return canvas.toDataURL();
        });
    }

    _createMapHeaders() {
        if (this.props['temp_display'] == 'None') {
            return ['', ''];
        }
        
        const filters = Object.values(this.props['filters']).filter(f => f.display(this.props['units']) !== null);
        const filter_str = filters.map(filt => filt.toHeaderString(this.props['units'])).join(', ');
        const partition_filter_str = this.props['filters']['partition'].toHeaderString() + ' ';
        const data_limits = Interval.fromDateTimes(this.props['data_limits'].start.plus(this.props['cday_offset']), 
                                                   this.props['data_limits'].end.plus(this.props['cday_offset']));

        const intv_string_parts = this.props['interval'].toString(this.props['cday_offset'], data_limits, true);

        return [partition_filter_str + 'Tornadoes ' + intv_string_parts['preposition'] + ' ' + intv_string_parts['string'], filter_str];
    }

    render() {
        const short_cutoff = 700;
        const [date_header_str, filter_str] = this._createMapHeaders();

        let env_data_str = "";

        if (this.props['env_type'] !== null && this.props['sel_data_time'] !== null) {
            const which_title = this.props['window_size']['width'] > short_cutoff ? 'normal' : 'short';
            const data_title = this.props['env_handler'].getTitle(this.props['env_type'], which_title);
            const valid_str = this.props['window_size']['width'] > short_cutoff ? ' Valid ' : ' @ ';
            const valid_fmt = this.props['window_size']['width'] > short_cutoff + 200 ? "HHmm 'UTC' MMM d, yyyy" : "HH'Z' MMM d";
            env_data_str = data_title + valid_str 
                + this.props['sel_data_time'].toFormat(valid_fmt);
        }

        const date_header_div = date_header_str === "" ? "": <div>{date_header_str}</div>
        const filter_div = filter_str === "" ? "": <div>{filter_str}</div>

        const visible_class = this.props['controls_visible'] ? '' : 'controls-hidden'
        const small_screen_class = this.props['window_size']['width'] > short_cutoff + 100 ? "" : "small-screen";

        const font_ems = Math.min(0.9, this.props['window_size']['width'] / 550);

        return (
            <div className={'overlay ' + visible_class} 
                ref={el => this.overlay = el}
                >
                <div id='map-header' style={{'fontSize': font_ems + 'rem'}}>
                    <div id='logo'><a href='https://tornadoarchive.com/home' target='_blank'><img src={logo}></img></a></div>
                    <div id='date-header'>
                        {date_header_div}
                        {filter_div}
                        <div>{env_data_str}</div>
                    </div>
                    <div id='statistics-header' className={small_screen_class}>
                        <div>
                            <div ref={el => this.ntor_el = el}></div>
                        </div>
                        <div>
                            <div ref={el => this.ninj_el = el}></div>
                            <div ref={el => this.nfat_el = el}></div>
                        </div>
                    </div>
                </div>
                <div id='attribution' className={visible_class + " " + small_screen_class} ref={el => this.attribution = el}></div>
            </div>
        );
    }
}

export default MapOverlayWidget;